import { Component, inject } from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PlanStatusService, PlanStatusCacheInfo } from '../../../shared/services/plan-status-cache.service';
import { ProfileProviderPfControllerService, ProfileProviderPjControllerService } from '../../../core/generated';
import { firstValueFrom } from 'rxjs';
import { ContainerComponent } from '../../../shared/container/container.component';

@Component({
    selector: 'app-admin-overview',
    standalone: true,
    imports: [MaterialModule, CommonModule, FormsModule, ContainerComponent],
    templateUrl: './admin-overview.component.html',
    styleUrls: ['./admin-overview.component.scss']
})
export class AdminOverviewComponent {
  private planStatusService = inject(PlanStatusService);
  private profileProviderPFService = inject(ProfileProviderPfControllerService);
  private profileProviderPJService = inject(ProfileProviderPjControllerService);

  query: string = '';
  loading = false;
  error: string | null = null;
  results: PlanStatusCacheInfo[] = [];
  searched = false;

  async search(): Promise<void> {
    this.error = null;
    this.results = [];
    this.searched = false;

    const q = (this.query || '').trim();
    if (!q) {
      this.error = 'Informe um CPF, CNPJ ou email para consulta.';
      return;
    }

    this.loading = true;

    try {
      const isEmail = q.includes('@');
      const collected: PlanStatusCacheInfo[] = [];

      if (isEmail) {
        // Buscar perfis PF e PJ por email diretamente no backend
        try {
          // Buscar perfil PF
          const profilePF = await firstValueFrom(this.profileProviderPFService.findByUserEmail1(q));
          if (profilePF && profilePF.cpf) {
            const pfPlans = await this.planStatusService.fetchForCpfCnpj(profilePF.cpf);
            collected.push(...pfPlans);
          }
        } catch (err) {
          console.log('Nenhum perfil PF encontrado para este email');
        }

        try {
          // Buscar perfis PJ
          const profilesPJ = await firstValueFrom(this.profileProviderPJService.findByUserEmail(q));
          if (profilesPJ && profilesPJ.length > 0) {
            // Filtrar perfis PJ que correspondem ao email
            const matchingProfiles = profilesPJ.filter((p: any) => 
              p.email === q || (p.users && p.users.some((u: any) => u.email === q))
            );
            
            if (matchingProfiles.length > 0) {
              const cnpjs = matchingProfiles.map((p: any) => p.cnpj).filter(Boolean);
              const promises = cnpjs.map((cnpj: string) => this.planStatusService.fetchForCpfCnpj(cnpj));
              const results = await Promise.all(promises);
              results.forEach(r => collected.push(...r));
            }
          }
        } catch (err) {
          console.log('Nenhum perfil PJ encontrado para este email');
        }
      } else {
        // Buscar diretamente por CPF/CNPJ
        const plans = await this.planStatusService.fetchForCpfCnpj(q);
        collected.push(...plans);
      }

      // Deduplicate por cpfCnpj + planId
      const map = new Map<string, PlanStatusCacheInfo>();
      collected.forEach(p => {
        const key = `${p.cpfCnpj}::${p.planId}`;
        if (!map.has(key)) map.set(key, p);
      });

      this.results = Array.from(map.values());
      this.searched = true;

      if (this.results.length === 0 && !this.error) {
        this.error = 'Nenhum plano encontrado para este critério de busca.';
      }
    } catch (err: any) {
      console.error('AdminOverview: erro ao buscar', err);
      this.error = err?.message || 'Erro ao consultar planos. Tente novamente.';
    } finally {
      this.loading = false;
    }
  }

  clear() {
    this.query = '';
    this.results = [];
    this.error = null;
    this.searched = false;
  }
}
